/* eslint-disable max-lines */
import {
  AttendanceVerificationMethod,
  type TakeAVideoConfig,
} from "@clipboard-health/contract-backend-main";
import { type WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { type ScoreFreezeReason } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import type { FacilityRule } from "@src/appV2/Facilities/types";
import { useToast } from "@src/appV2/lib";
import { BreakConfirmationBottomSheet } from "@src/appV2/redesign/Shift/BreakConfirmationBottomSheet/BreakConfirmationBottomSheet";
import { useSkippableWait } from "@src/appV2/redesign/utils/useSkippableWait";
import {
  getWorkerShiftPath,
  type GetWorkerShiftResponse,
} from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import { MY_SHIFTS_BACKGROUND_CHECK_ERROR_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import { isBackgroundCheckError } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/utils/handleBackgroundCheckError";
import { NoteAcknowledgementAction } from "@src/appV2/Shifts/MandatoryBreakPolicy/types";
import { DEFAULT_TIMECLOCK_COMPLIANCE_VIDEO_CONFIG } from "@src/appV2/Shifts/Shift/ShiftState/api/useGetShiftState";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import type { NfcTagRequest, WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useCallback, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../../api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  ShiftStage,
  type TimekeepingData,
  useRecordShiftTimekeepingAction,
} from "../../api/useRecordShiftTimekeepingAction";
import { NfcHashValidationAction } from "../NfcValidation/api/types";
import { WorkerNfcValidation } from "../NfcValidation/WorkerNfcValidation";
import {
  type ComplianceProofVideo,
  TimeclockVideoProofFlow,
} from "../TimeclockComplianceVideoProof";
import { AcceptNonInstantPayContent } from "./AcceptNonInstantPayContent";
import { DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS } from "./constants";
import { NfcNotSupportedContent } from "./NfcNotSupportedContent";
import { TimekeepingActionSuccessContent } from "./TimekeepingActionSuccessContent";
import { getClockInSuccessDescription } from "./utils/getClockInSuccessDescription";
import { getHasWorkerClockedInOnTime } from "./utils/getHasWorkerClockedInOnTime";
import { WorkplaceRulesReminderContent } from "./WorkplaceRulesReminderContent";

export interface ClockInTimeclockComplianceBottomSheetProps {
  modalState: UseModalState;
  shift: Pick<WorkerShift, "_id" | "start">;
  attendanceVerificationMethod: TakeAVideoConfig;
  workerCredentials: {
    pin: string;
  };
  earliestGeofenceEntryTime?: Date;
  workerId: string;
  workplaceId: string;
  workerStage: WorkerStage;
  workerSubstage?: string;
  workplaceName: string;
  requiresBreakPolicyAcknowledgement: boolean;
  breakPolicyNoteId: string;
  workplaceRules?: FacilityRule[];
  shouldShowWorkplaceRulesReminder: boolean;
  isNfcEnabled: boolean;
  nfcTagRequests: NfcTagRequest[];
  shiftState: ShiftStateData;
  isSecondShiftInDouble: boolean;
  targetArrivalTime?: Date;
  shiftStartDate?: Date;
  timezone?: string;
  shiftBookedAt?: Date;
  isTimesheetEditDisabled: boolean;
  shouldEnforceNfcTimesheetBounds: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  isClipboardScoreFreezeEnabled: boolean;
  clipboardScoreFreezeReason?: ScoreFreezeReason;
  isMissedPunchRequestEnabled: boolean;
  onTimekeepingActionCompleted: (props?: { nonInstantPayReason?: string }) => void;
  onTimekeepingActionFailed: (props: { error: string }) => void;
  onClockInFlowCompleted: () => void;
}

type TimeclockComplianceStep =
  | "video-recording"
  | "nfc-validation"
  | "nfc-not-supported"
  | "requesting-clock-in"
  | "clock-in-success"
  | "accept-non-instant-pay"
  | "requesting-non-instant-pay"
  | "workplace-rules";

export function ClockInTimeclockComplianceBottomSheet(
  props: ClockInTimeclockComplianceBottomSheetProps
) {
  const {
    modalState,
    workerId,
    workerStage,
    workerSubstage,
    workplaceId,
    shift,
    attendanceVerificationMethod,
    workerCredentials,
    workplaceName,
    requiresBreakPolicyAcknowledgement: requiresBreakPolicyAcknowledgementProperty,
    breakPolicyNoteId,
    workplaceRules,
    shouldShowWorkplaceRulesReminder,
    isNfcEnabled,
    nfcTagRequests,
    shiftState,
    isSecondShiftInDouble,
    earliestGeofenceEntryTime,
    targetArrivalTime,
    shiftStartDate,
    timezone,
    shiftBookedAt,
    isTimesheetEditDisabled,
    shouldEnforceNfcTimesheetBounds,
    isArrivalTimeFeatureEnabled,
    isClipboardScoreFreezeEnabled,
    clipboardScoreFreezeReason,
    isMissedPunchRequestEnabled,
    onTimekeepingActionCompleted,
    onTimekeepingActionFailed,
    onClockInFlowCompleted,
  } = props;

  const shiftId = shift._id;

  const history = useHistory();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(workerId);

  const [requiresBreakPolicyAcknowledgement, setRequiresBreakPolicyAcknowledgement] = useState(
    requiresBreakPolicyAcknowledgementProperty
  );

  const [step, setStep] = useState<TimeclockComplianceStep>("video-recording");
  const complianceProofRef = useRef<ComplianceProofVideo>();

  const { mutateAsync: recordTimekeepingAction, data: postClockInShift } =
    useRecordShiftTimekeepingAction();

  const { wait, skip } = useSkippableWait();

  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();

  const clockInTime = postClockInShift?.clockInOut?.start
    ? parseISO(postClockInShift.clockInOut.start)
    : undefined;

  const hasWorkerClockedInOnTime = getHasWorkerClockedInOnTime({
    shiftStartDate: parseISO(shift.start),
    clockInTime,
  });

  const abortFlowAndClose = useCallback(() => {
    skip();
    modalState.closeModal();
    setStep("video-recording");
  }, [modalState, skip]);

  const completeSuccessFlow = useCallback(
    async (timekeepingData?: TimekeepingData) => {
      setStep("clock-in-success");
      await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);

      if (timekeepingData) {
        queryClient.setQueryData<GetWorkerShiftResponse>([getWorkerShiftPath(shiftId)], (result) =>
          result
            ? {
                ...result,
                response: {
                  ...result.response,
                  clockInOut: timekeepingData.clockInOut,
                },
              }
            : undefined
        );
        onClockInFlowCompleted();
      }
    },
    [wait, queryClient, shiftId, onClockInFlowCompleted]
  );

  const onClockInSuccess = useCallback(
    async (timekeepingData: TimekeepingData) => {
      onTimekeepingActionCompleted();

      if (shouldShowWorkplaceRulesReminder && workplaceRules && workplaceRules.length > 0) {
        setStep("workplace-rules");
      } else {
        await completeSuccessFlow(timekeepingData);
      }
    },
    [
      completeSuccessFlow,
      shouldShowWorkplaceRulesReminder,
      workplaceRules,
      onTimekeepingActionCompleted,
    ]
  );

  async function handleRecordClockIn(
    actionCheckType: ActionCheckType,
    videoProof: ComplianceProofVideo | undefined
  ) {
    setStep("requesting-clock-in");

    try {
      const postMutationShift = await recordTimekeepingAction({
        shiftId,
        stage: ShiftStage.CLOCK_IN,
        shiftActionCheck: actionCheckType,
        verificationMethod: AttendanceVerificationMethod.TAKE_A_VIDEO,
        complianceProof: videoProof,
      });

      await onClockInSuccess(postMutationShift);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock in, please try again.";

      onTimekeepingActionFailed({ error: errorMessage });

      if (isBackgroundCheckError(error)) {
        modalState.closeModal();
        history.replace(MY_SHIFTS_BACKGROUND_CHECK_ERROR_FULL_PATH);
        return;
      }

      setStep("accept-non-instant-pay");
    }
  }

  async function convertToNonInstantPay() {
    try {
      await markNonInstant({
        shiftId,
        reason: ShiftMarkedNonIpReasons.TIMECLOCK_VIDEO_PROOF_COMPLIANCE_FAILURE,
      });

      onTimekeepingActionCompleted({
        nonInstantPayReason: "TIMECLOCK_VIDEO_PROOF_COMPLIANCE_FAILURE",
      });

      modalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      abortFlowAndClose();
    }
  }

  if (requiresBreakPolicyAcknowledgement) {
    return (
      <BreakConfirmationBottomSheet
        action={NoteAcknowledgementAction.CLOCK_IN}
        isLoading={false}
        modalState={modalState}
        shiftId={shiftId}
        workplaceId={workplaceId}
        breakPolicyNoteId={breakPolicyNoteId}
        onAcknowledge={async () => {
          setRequiresBreakPolicyAcknowledgement(false);
        }}
      />
    );
  }

  if (step === "video-recording" && modalState.modalIsOpen) {
    return (
      <TimeclockVideoProofFlow
        workplaceName={workplaceName}
        timeclockLocation={attendanceVerificationMethod.timeclockLocationString}
        timeclockPin={workerCredentials.pin}
        timeclockGifUrl={attendanceVerificationMethod.timeclockGifUrl}
        workplaceId={workplaceId}
        shiftId={shiftId}
        workerId={workerId}
        clockAction="clock-in"
        videoConfig={DEFAULT_TIMECLOCK_COMPLIANCE_VIDEO_CONFIG}
        onSuccess={async (videoProof: ComplianceProofVideo) => {
          complianceProofRef.current = videoProof;

          if (isNfcEnabled) {
            setStep("nfc-validation");
          } else {
            await handleRecordClockIn(ActionCheckType.LOCATION, videoProof);
          }
        }}
        onCancel={() => {
          abortFlowAndClose();
        }}
      />
    );
  }

  return (
    <BottomSheet modalState={modalState} canDismiss={false}>
      {step === "nfc-validation" && (
        <WorkerNfcValidation
          nfcTagRequests={nfcTagRequests}
          shiftId={shiftId}
          metaProps={{
            workerId,
            workplaceId,
            workplaceName,
          }}
          clockAction={NfcHashValidationAction.CLOCK_IN}
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          onSuccess={async () => {
            await handleRecordClockIn(ActionCheckType.NFC, complianceProofRef.current);
          }}
          onNfcNotSupported={() => {
            setStep("nfc-not-supported");
          }}
          onSkipLocationCheck={() => {
            setStep("accept-non-instant-pay");
          }}
          onCancel={() => {
            abortFlowAndClose();
          }}
        />
      )}

      {step === "nfc-not-supported" && (
        <NfcNotSupportedContent
          workplaceName={workplaceName}
          onRetryNfc={() => {
            setStep("nfc-validation");
          }}
          onConvertToNonInstantPay={() => {
            setStep("accept-non-instant-pay");
          }}
          onCancel={() => {
            abortFlowAndClose();
          }}
        />
      )}

      {step === "requesting-clock-in" && (
        <BottomSheetLoadingContent
          title="Recording your clock in"
          description="This may take a moment"
        />
      )}

      {step === "clock-in-success" && (
        <TimekeepingActionSuccessContent
          title="Have a great shift!"
          description={getClockInSuccessDescription({
            hasWorkerClockedInOnTime,
            isSecondShiftInDouble,
            earliestGeofenceEntryTime,
            isTimesheetEditDisabled,
            shouldEnforceNfcTimesheetBounds,
            isArrivalTimeFeatureEnabled,
            targetArrivalTime,
            shiftStartDate,
            clockInTime,
            timezone,
            shiftBookedAt,
            policy: clipboardScoreProfile?.policy,
            isClipboardScoreFreezeEnabled,
            clipboardScoreFreezeReason,
            workerStage,
            workerSubstage,
          })}
          onClose={() => {
            skip();
          }}
        />
      )}

      {step === "workplace-rules" && workplaceRules && (
        <WorkplaceRulesReminderContent
          workplaceRules={workplaceRules}
          shiftId={shiftId}
          workplaceId={workplaceId}
          onContinue={async () => {
            await completeSuccessFlow(postClockInShift);
          }}
        />
      )}

      {step === "accept-non-instant-pay" && (
        <AcceptNonInstantPayContent
          isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
          onAccept={async () => {
            setStep("requesting-non-instant-pay");
            await convertToNonInstantPay();
          }}
          onCancel={() => {
            // When worker cancels the non-instant pay conversion, return them to the previous step
            // i.e. retry NFC validation or close (geofence was already checked before modal opened)
            if (isNfcEnabled) {
              setStep("nfc-validation");
            } else {
              abortFlowAndClose();
            }
          }}
        />
      )}

      {step === "requesting-non-instant-pay" && (
        <BottomSheetLoadingContent
          title="Converting to non-instant pay"
          description="This may take a moment"
        />
      )}
    </BottomSheet>
  );
}
/* eslint-enable max-lines */
